/**
 * Chart geometry depends on measured text width: axis label widths determine the
 * plot rect, which in turn positions every bar, line vertex, and end label. If a
 * chart renders before the brand woff2 files finish loading, the renderer measures
 * fallback-font metrics and the layout shifts slightly once the real font swaps in.
 *
 * That race made chart output nondeterministic between page loads. Gating the first
 * render on font readiness makes brand metrics the only outcome.
 *
 * Best-effort by design: the SDK does not ship the fonts (host pages declare them,
 * see `dist/fonts.css`), so a host that never loads them must still get a chart.
 * After {@link FONT_READY_TIMEOUT_MS} we proceed with whatever is available.
 */

/** Faces that affect measured text in charts (see `docs/build.mjs` fontFiles). */
const CHART_FONT_SPECS = [
  '300 11px "Sohne Mono"',
  '300 11px "Sohne"',
  '500 11px "Sohne"',
  '300 16px "Ivory LL"',
] as const;

/** Cap on how long a render waits for brand fonts before falling back. */
export const FONT_READY_TIMEOUT_MS = 3000;

let pending: Promise<void> | null = null;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

async function loadChartFonts(): Promise<void> {
  if (typeof document === "undefined") return;
  const fontSet = (document as Document & { fonts?: FontFaceSet }).fonts;
  if (!fontSet || typeof fontSet.load !== "function") return;

  // `document.fonts.ready` alone is not enough: it can settle before these faces
  // are ever requested, since a face is only fetched once something needs it.
  // Requesting them explicitly first is what makes the wait meaningful.
  await Promise.all(
    CHART_FONT_SPECS.map((spec) =>
      fontSet.load(spec).catch(() => {
        /* face unavailable on this host — fall back to system metrics */
      })
    )
  );
  await fontSet.ready;
}

/**
 * Resolve once brand fonts are loaded, or after {@link FONT_READY_TIMEOUT_MS}.
 * Memoized: the wait happens once per document, not once per chart.
 */
export function ensureChartFontsReady(): Promise<void> {
  pending ??= Promise.race([loadChartFonts(), delay(FONT_READY_TIMEOUT_MS)]).then(
    () => undefined
  );
  return pending;
}

/** Test seam: drop the memoized wait so a suite can exercise the gate repeatedly. */
export function resetChartFontsReadyForTests(): void {
  pending = null;
}
